import { type Dispatch, type SetStateAction, useState } from "react";

import {
  type WorkflowStepInput,
  type WorkflowTemplate,
  WorkflowStepStatuses,
} from "~/api/types";
import { useWorkflowUsers } from "~/hooks/useWorkflowUsers";

const DEFAULT_STEP: WorkflowStepInput = {
  _key: "default-0",
  name: "Step 1",
  description: "",
  status: WorkflowStepStatuses.upcoming,
  type: "review",
  order: 0,
  dueAt: null,
  users: [],
};

interface TemplateEditorState {
  editedSteps: WorkflowStepInput[];
  setEditedSteps: Dispatch<SetStateAction<WorkflowStepInput[]>>;
  editedName: string;
  editedDescription: string | null;
  editName: (name: string) => void;
  editDescription: (description: string | null) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  isSaving: boolean;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  syncToTemplate: (template: WorkflowTemplate) => void;
  loadFromTemplate: (template: WorkflowTemplate) => void;
  resetForNewTemplate: () => void;
  clearLoadedTemplate: () => void;
}

export function useTemplateEditor(): TemplateEditorState {
  const { hydrateSteps } = useWorkflowUsers();

  const [editedSteps, setEditedSteps] = useState<WorkflowStepInput[]>([]);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState<string | null>(
    null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastLoadedTemplateId, setLastLoadedTemplateId] = useState<
    number | null
  >(null);

  function loadFromTemplate(template: WorkflowTemplate): void {
    const steps = hydrateSteps(template);
    if (!steps) return;

    setEditedSteps(steps);
    setEditedName(template.name ?? "");
    setEditedDescription(template.description);
    setHasUnsavedChanges(false);
    setLastLoadedTemplateId(template.id);
  }

  function syncToTemplate(template: WorkflowTemplate): void {
    if (lastLoadedTemplateId !== template.id) {
      loadFromTemplate(template);
    }
  }

  function editName(name: string): void {
    setEditedName(name);
    setHasUnsavedChanges(true);
  }

  function editDescription(description: string | null): void {
    setEditedDescription(description);
    setHasUnsavedChanges(true);
  }

  function resetForNewTemplate(): void {
    setEditedSteps([{ ...DEFAULT_STEP }]);
    setEditedName("");
    setEditedDescription(null);
    setHasUnsavedChanges(false);
    setLastLoadedTemplateId(null);
  }

  return {
    editedSteps,
    setEditedSteps,
    editedName,
    editedDescription,
    editName,
    editDescription,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isSaving,
    setIsSaving,
    syncToTemplate,
    loadFromTemplate,
    resetForNewTemplate,
    clearLoadedTemplate: () => setLastLoadedTemplateId(null),
  };
}
